import React from 'react';
import { ArrowDownCircle, ArrowUpCircle, ReceiptText, Users } from 'lucide-react';
import { formatNumber } from '../../utils/formatters';
import { CURRENCY_ORDER, CURRENCY_META } from '../../constants/currencies';

const tones = {
  blue: {
    icon: 'bg-blue-50 text-blue-600',
    row: 'bg-blue-50/70 text-blue-700 border-blue-100'
  },
  emerald: {
    icon: 'bg-emerald-50 text-emerald-600',
    row: 'bg-emerald-50/70 text-emerald-700 border-emerald-100'
  },
  rose: {
    icon: 'bg-rose-50 text-rose-600',
    row: 'bg-rose-50/70 text-rose-700 border-rose-100'
  },
  amber: {
    icon: 'bg-amber-50 text-amber-600',
    row: 'bg-amber-50/70 text-amber-700 border-amber-100'
  }
};

const getCurrencyValue = (source, currency, metricKey) => {
  if (!source) return 0;

  if (metricKey) {
    return Number(source[currency]?.[metricKey] || source[metricKey]?.[currency] || 0);
  }

  return Number(source[currency] || 0);
};

const MoneyRows = ({ source, metricKey, tone }) => (
  <div className="mt-4 space-y-2">
    {CURRENCY_ORDER.map((currency) => {
      const value = getCurrencyValue(source, currency, metricKey);
      const meta = CURRENCY_META[currency] || CURRENCY_META.TRY;

      return (
        <div
          key={currency}
          className={`flex items-center justify-between gap-3 rounded-2xl border px-4 py-3 ${tones[tone].row}`}
        >
          <span className="text-[10px] font-black uppercase tracking-widest">{meta.label}</span>
          <span className="text-sm font-black tabular-nums">
            {meta.symbol} {formatNumber(value)}
          </span>
        </div>
      );
    })}
  </div>
);

const TryTotal = ({ value }) => (
  <p className="mt-3 text-2xl font-black text-slate-900 tracking-tight tabular-nums">
    ₺ {formatNumber(value)}
  </p>
);

const FinancialCard = ({ title, icon, tone, children }) => (
  <div className="bg-white border border-slate-100 rounded-[2rem] p-6 shadow-sm">
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{title}</p>
      </div>
      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${tones[tone].icon}`}>
        {React.createElement(icon, { size: 24 })}
      </div>
    </div>
    {children}
  </div>
);

const resolveByCurrency = (source = {}) => {
  if (source.byCurrency) return source.byCurrency;
  return CURRENCY_ORDER.reduce((acc, currency) => {
    acc[currency] = Number(source[currency] || 0);
    return acc;
  }, {});
};

const FinancialSummaryCards = ({
  openInvoices = {},
  cariMetrics = {},
  personnelBalances = {}
}) => {
  const receivables = cariMetrics.receivables || { tryTotal: 0, byCurrency: cariMetrics.credit || {} };
  const payables = cariMetrics.payables || { tryTotal: 0, byCurrency: cariMetrics.debit || {} };

  return (
    <>
      <FinancialCard title="Bekleyen Açık Faturalar" icon={ReceiptText} tone="blue">
        <TryTotal value={openInvoices.tryTotal} />
        <p className="mt-1 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Konsolide TRY</p>
        <MoneyRows source={resolveByCurrency(openInvoices)} tone="blue" />
      </FinancialCard>
      <FinancialCard title="Toplam Cari Alacaklar" icon={ArrowUpCircle} tone="emerald">
        <TryTotal value={receivables.tryTotal} />
        <p className="mt-1 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Açık satış faturaları</p>
        <MoneyRows source={resolveByCurrency(receivables)} tone="emerald" />
      </FinancialCard>
      <FinancialCard title="Toplam Cari Borçlar" icon={ArrowDownCircle} tone="rose">
        <TryTotal value={payables.tryTotal} />
        <p className="mt-1 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Açık alış faturaları</p>
        <MoneyRows source={resolveByCurrency(payables)} tone="rose" />
      </FinancialCard>
      <FinancialCard title="Personel Bakiyeleri" icon={Users} tone="amber">
        <div className="mt-4 space-y-4">
          <div>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Personele borç</p>
            <TryTotal value={personnelBalances.payableTry} />
            <p className="mt-1 text-[10px] font-bold text-amber-700/70">Hakediş − ödeme çıkışları</p>
          </div>
          <div>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Personelden alacak</p>
            <TryTotal value={personnelBalances.receivableTry} />
            <p className="mt-1 text-[10px] font-bold text-amber-700/70">Avans / zimmet bakiyesi</p>
          </div>
        </div>
      </FinancialCard>
    </>
  );
};

export default FinancialSummaryCards;
